// Admin → Reports (V4-SH): what people reported on share pages (also emailed to abuse@). Each report: watch the
// link, then dismiss it, turn the link off (the owner gets an email saying why) or remove the reported comment
// (and the review note it became). Admins only.
import { useState } from "react";
import { Empty, ErrorNote, Spinner, timeAgo, useLoad } from "../components/bits";
import { Shell } from "../components/Shell";
import { playNow } from "../player";
import { Link } from "../router";
import { fmtAt, shareSource, type ReportAction, type ShareReport } from "../share";
import { toast, useStore } from "../store";

const DONE: Record<ReportAction, string> = { dismiss: "Dismissed", turn_off_link: "Link turned off", remove_comment: "Comment removed" };

export function AdminReportsScreen() {
  const me = useStore((s) => s.me);
  const [all, setAll] = useState(false);
  const { data, error, loading, reload, setData } = useLoad(async () => (await shareSource()).reports(all), [all]);
  if (me && me.user.role !== "admin")
    return (
      <Shell>
        <div className="page">
          <Empty title="Admins only" action={<Link to="/" className="btn btn-quiet">Go home</Link>}>
            This page holds what people reported on share links.
          </Empty>
        </div>
      </Shell>
    );
  return (
    <Shell>
      <div className="page reports">
        <header className="page-head">
          <div>
            <h1 className="page-title">Reports</h1>
            <p className="page-lede">What people reported on share links. Aim to act within 48 hours.</p>
          </div>
          <span className="seg" role="radiogroup" aria-label="Show">
            <button type="button" role="radio" aria-checked={!all} className={!all ? "is-on" : ""} onClick={() => setAll(false)}>
              Open
            </button>
            <button type="button" role="radio" aria-checked={all} className={all ? "is-on" : ""} onClick={() => setAll(true)}>
              All
            </button>
          </span>
        </header>
        {error && <ErrorNote text={`Couldn't load the reports. ${error}`} retry={reload} />}
        {loading && !data && <Spinner label="Loading reports" />}
        {data && !data.length && <Empty title={all ? "No reports yet" : "Nothing to review"}>{all ? "Reports from share pages land here." : "Every report has been handled."}</Empty>}
        {data && data.length > 0 && (
          <ul className="report-list">
            {data.map((r) => (
              <ReportCard
                key={r.id}
                r={r}
                onDone={(next) => {
                  // a link turned off settles every open report on it
                  setData(data.map((x) => (x.id === next.id ? next : next.action === "turn_off_link" && x.link.id === next.link.id && x.status === "open" ? { ...x, status: "actioned", action: "turn_off_link", link: next.link } : x)));
                }}
              />
            ))}
          </ul>
        )}
      </div>
    </Shell>
  );
}

function ReportCard({ r, onDone }: { r: ShareReport; onDone: (r: ShareReport) => void }) {
  const [busy, setBusy] = useState<ReportAction | null>(null);
  const act = async (a: ReportAction) => {
    setBusy(a);
    try {
      onDone(await (await shareSource()).act(r.id, a));
      toast(DONE[a], "good");
    } catch (e) {
      toast(`Couldn't do that. ${(e as Error).message}`, "bad");
    } finally {
      setBusy(null);
    }
  };
  const open = r.status === "open";
  return (
    <li className={`report-card${open ? "" : " is-done"}`}>
      <header className="report-head">
        <span className="tag tag-accent">{r.reasonLabel}</span>
        <span className="muted">{timeAgo(r.createdAt)}</span>
        {!open && <span className="tag">{r.action ? DONE[r.action] : r.status}</span>}
      </header>
      <p className="report-what">
        <strong>{r.link.title}</strong>
        {r.link.version ? ` v${r.link.version}` : ""} by {r.link.ownerEmail}
        {r.link.state !== "live" && <span className="muted"> · link {r.link.state === "off" ? "off" : "expired"}</span>}
      </p>
      {r.comment && (
        <blockquote className="report-quote">
          <span className="muted">
            {r.comment.name} at {fmtAt(r.comment.atS)}:
          </span>{" "}
          {r.comment.body}
        </blockquote>
      )}
      {r.details && <p className="report-details">{r.details}</p>}
      {r.email && <p className="muted">Reporter: {r.email}</p>}
      <div className="report-acts">
        {r.link.videoUrl && (
          <button type="button" className="btn btn-quiet btn-small" onClick={() => playNow({ url: r.link.videoUrl!, title: `${r.link.title}${r.link.version ? `, v${r.link.version}` : ""} (reported)`, what: "render" })}>
            Watch it
          </button>
        )}
        <a className="btn btn-quiet btn-small" href={r.link.url} target="_blank" rel="noopener noreferrer">
          Open the page
        </a>
        {open && (
          <>
            {r.comment && (
              <button type="button" className="btn btn-quiet btn-small" disabled={!!busy} onClick={() => void act("remove_comment")}>
                Remove the comment
              </button>
            )}
            {r.link.state === "live" && (
              <button type="button" className="btn btn-danger btn-small" disabled={!!busy} onClick={() => void act("turn_off_link")}>
                Turn the link off
              </button>
            )}
            <button type="button" className="btn btn-quiet btn-small" disabled={!!busy} onClick={() => void act("dismiss")}>
              Dismiss
            </button>
          </>
        )}
      </div>
    </li>
  );
}
